import { TaxProfileFieldKeyEnum } from "@repo/graphql/gql/graphql";
import type {
  BooleanFormName,
  RegimeOption,
  TaxProfileFieldData,
  TaxProfileFormValues,
  TextFormName,
} from "./types";

/**
 * The form to show: the selected regime's complete form, or the one the
 * country offers before a regime is picked.
 */
export const selectActiveFields = (
  requirementsFields: ReadonlyArray<TaxProfileFieldData>,
  selectedRegime: RegimeOption | null,
): ReadonlyArray<TaxProfileFieldData> =>
  selectedRegime?.fields ?? requirementsFields;

export const findRegime = (
  regimes: ReadonlyArray<RegimeOption>,
  code: TaxProfileFormValues["taxRegime"],
): RegimeOption | null =>
  regimes.find((regime) => regime.code === code) ?? null;

export const findField = (
  fields: ReadonlyArray<TaxProfileFieldData>,
  key: TaxProfileFieldKeyEnum,
): TaxProfileFieldData | undefined => fields.find((field) => field.key === key);

export const hasField = (
  fields: ReadonlyArray<TaxProfileFieldData>,
  key: TaxProfileFieldKeyEnum,
): boolean => findField(fields, key) !== undefined;

const TEXT_FORM_NAMES: Partial<Record<TaxProfileFieldKeyEnum, TextFormName>> = {
  [TaxProfileFieldKeyEnum.LegalName]: "legalName",
  [TaxProfileFieldKeyEnum.BusinessActivity]: "businessActivity",
  [TaxProfileFieldKeyEnum.Email]: "email",
  [TaxProfileFieldKeyEnum.AddressLine]: "addressLine",
};

const BOOLEAN_FORM_NAMES: Partial<
  Record<TaxProfileFieldKeyEnum, BooleanFormName>
> = {
  [TaxProfileFieldKeyEnum.IsLargeTaxpayer]: "isLargeTaxpayer",
  [TaxProfileFieldKeyEnum.IsMunicipalWithholdingAgent]:
    "isMunicipalWithholdingAgent",
};

export const textFormName = (
  key: TaxProfileFieldKeyEnum,
): TextFormName | undefined => TEXT_FORM_NAMES[key];

export const booleanFormName = (
  key: TaxProfileFieldKeyEnum,
): BooleanFormName | undefined => BOOLEAN_FORM_NAMES[key];

/** Where each field keeps its value in the form; the address has its own form. */
export const formNameForKey = (
  key: TaxProfileFieldKeyEnum,
): keyof TaxProfileFormValues | undefined => {
  switch (key) {
    case TaxProfileFieldKeyEnum.TaxId:
      return "taxId";
    case TaxProfileFieldKeyEnum.TaxRegime:
      return "taxRegime";
    case TaxProfileFieldKeyEnum.Address:
      return undefined;
    default:
      return textFormName(key) ?? booleanFormName(key);
  }
};
